import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { BuscaTodosProdutosDev, api } from '../../../../services/api'
import { useToast } from '@chakra-ui/react'

const ProdutosContext = createContext({})

export function ProdutosProvider({ children }) {
  const [isDesativando, setIsDesativando] = useState(null)
  const [queryParams, setQueryParams] = useState({
    buscar: '',
    page: 1,
    per_page: 10000,
  })

  const queryClient = useQueryClient()
  const toast = useToast()

  const { data, isLoading, isError, refetch, isFetching } = useQuery(
    ['todosProdutos', queryParams],
    async () => {
      console.log('Executando consulta com params:', queryParams)
      const response = await BuscaTodosProdutosDev.get('', {
        params: {
          page: queryParams.page,
          per_page: queryParams.per_page,
          buscar: queryParams.buscar || undefined,
        },
      })
      console.log('Resposta da API:', response.data)
      return response.data
    },
    {
      keepPreviousData: true,
      staleTime: 30000, // 30 segundos
      refetchOnWindowFocus: false,
      onError: (error) => {
        console.error('Erro ao buscar produtos:', error)
        toast({
          title: 'Erro',
          description: 'Não foi possível carregar os produtos. Tente novamente.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        })
      },
    }
  )

  useEffect(() => {
    if (!isLoading && !isFetching && data) {
      if (data.current_page === queryParams.page) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        })
      }
    }
  }, [isLoading, isFetching, data, queryParams.page])

  const produtos = useMemo(() => {
    if (!data || !data.dados) return []
    return data.dados || []
  }, [data])

  const totalProdutos = useMemo(() => {
    if (!data) return 0
    return data.total || 0
  }, [data])

  const totalPages = useMemo(() => {
    if (!data) return 1
    return data.last_page || 1
  }, [data])

  const currentPage = useMemo(() => {
    if (!data) return queryParams.page
    return data.current_page || queryParams.page
  }, [data, queryParams.page])

  const setBusca = useCallback((termo) => {
    console.log('Definindo nova busca:', termo)
    setQueryParams((prev) => ({
      ...prev,
      buscar: termo,
      page: 1, // Voltar para a primeira página
    }))
  }, [])

  const handlePageChange = useCallback(
    (newPage) => {
      if (newPage > 0 && (!totalPages || newPage <= totalPages)) {
        setQueryParams((prev) => ({
          ...prev,
          page: newPage,
        }))
      }
    },
    [totalPages]
  )

  const handlePerPageChange = useCallback((newPerPage) => {
    const numPerPage = Number(newPerPage)
    setQueryParams((prev) => ({
      ...prev,
      per_page: numPerPage,
      page: 1, // Voltar para a primeira página
    }))
  }, [])

  const desativarProdutoMutation = useMutation(
    async (codigoProduto) => {
      setIsDesativando(codigoProduto)

      const response = await api.put('/site/v2/busca/status-produto', {
        codigo_produto: codigoProduto,
        status: 'N',
      })

      return {
        success: response.status === 200,
        codigo: codigoProduto,
        data: response.data,
      }
    },
    {
      onSuccess: (result) => {
        queryClient.invalidateQueries(['todosProdutos'])

        toast({
          title: 'Produto desativado',
          description: `O produto ${result.codigo} foi desativado com sucesso.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        })

        setIsDesativando(null)
      },
      onError: (error, codigoProduto) => {
        console.error('Erro ao desativar produto:', error)

        toast({
          title: 'Erro',
          description: `Não foi possível desativar o produto ${codigoProduto}. ${error.response?.data?.message || 'Tente novamente mais tarde.'}`,
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        })

        setIsDesativando(null)
      },
    }
  )

  const desativarProduto = useCallback(
    (codigoProduto) => {
      desativarProdutoMutation.mutate(codigoProduto)
    },
    [desativarProdutoMutation]
  )

  const recarregarDados = useCallback(() => {
    refetch()
  }, [refetch])

  const contextValue = useMemo(
    () => ({
      produtos,
      totalProdutos,
      loading: isLoading,
      isFetching,
      isError,
      setBusca,
      desativarProduto,
      refetch: recarregarDados,
      isDesativando,
      page: currentPage,
      setPage: handlePageChange,
      totalPages,
      perPage: queryParams.per_page,
      setPerPage: handlePerPageChange,
      queryParams,
    }),
    [
      produtos,
      totalProdutos,
      isLoading,
      isFetching,
      isError,
      setBusca,
      desativarProduto,
      recarregarDados,
      isDesativando,
      currentPage,
      handlePageChange,
      totalPages,
      queryParams.per_page,
      handlePerPageChange,
      queryParams,
    ]
  )

  return <ProdutosContext.Provider value={contextValue}>{children}</ProdutosContext.Provider>
}

export function useProdutosContext() {
  const context = useContext(ProdutosContext)
  if (context === undefined) {
    throw new Error('useProdutosContext deve ser usado dentro de um ProdutosProvider')
  }
  return context
}
